import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/features/power_projects/components/ui";
import {
  useCreateScopePrefix,
  useDeleteScopePrefix,
  useScopePrefixes,
  useUpdateScopePrefix,
} from "../hooks/useTenders";
import { WORK_SCOPE_LABEL } from "../types";
import type { TenderScopePrefix, TenderWorkScope } from "../types";

const SCOPE_OPTIONS = Object.entries(WORK_SCOPE_LABEL) as [
  Exclude<TenderWorkScope, "">,
  string,
][];

/** Podgląd tego, co backend zrobi z wpisanym prefiksem: wielkość liter, spacje i
 *  separatory znikają, a przy czystej kombinacji P/W kolejność liter przestaje mieć
 *  znaczenie („W + P" = „P+W"). Liczony lokalnie tylko dla podglądu — źródłem prawdy
 *  jest `normalized` zwrócone z zapisu. */
function previewNormalized(raw: string): string {
  const squashed = raw.toUpperCase().replace(/[^A-Z0-9]+/g, "");
  if (!squashed) return "";
  if (/^[PWI]+$/.test(squashed)) {
    return [...new Set(squashed.split("").filter((c) => c !== "I"))].sort().join("");
  }
  return squashed;
}

function PrefixRow({ row }: { row: TenderScopePrefix }) {
  const update = useUpdateScopePrefix();
  const remove = useDeleteScopePrefix();
  const [confirming, setConfirming] = useState(false);

  return (
    <tr className="border-t border-[var(--color-border-muted)]">
      <td className="px-3 py-2 font-mono text-sm text-[var(--color-text-primary)]">
        {row.prefix}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-muted)]">
        {row.normalized}
      </td>
      <td className="px-3 py-2">
        <select
          className="input text-sm"
          value={row.work_scope}
          disabled={update.isPending}
          onChange={(e) =>
            update.mutate({
              id: row.id,
              data: { work_scope: e.target.value as Exclude<TenderWorkScope, ""> },
            })
          }
        >
          {SCOPE_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
        {row.note || "—"}
      </td>
      <td className="px-3 py-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={row.is_active}
            disabled={update.isPending}
            onChange={(e) =>
              update.mutate({ id: row.id, data: { is_active: e.target.checked } })
            }
          />
          <span className="text-[var(--color-text-secondary)]">
            {row.is_active ? "aktywny" : "wyłączony"}
          </span>
        </label>
      </td>
      <td className="px-3 py-2 text-right">
        {confirming ? (
          <span className="inline-flex items-center gap-2">
            <button
              className="text-xs font-semibold text-red-600 hover:underline"
              onClick={() => remove.mutate(row.id)}
            >
              Usuń na pewno
            </button>
            <button
              className="text-xs text-[var(--color-text-tertiary)] hover:underline"
              onClick={() => setConfirming(false)}
            >
              Anuluj
            </button>
          </span>
        ) : (
          <button
            className="text-xs text-[var(--color-text-tertiary)] hover:text-red-500"
            onClick={() => setConfirming(true)}
          >
            Usuń
          </button>
        )}
      </td>
    </tr>
  );
}

export function TenderScopePrefixesPage() {
  const { data: rows, isLoading } = useScopePrefixes();
  const create = useCreateScopePrefix();

  const [prefix, setPrefix] = useState("");
  const [scope, setScope] = useState<Exclude<TenderWorkScope, "">>("wykonawstwo");
  const [note, setNote] = useState("");

  const preview = previewNormalized(prefix);
  const errors = create.error as
    | { response?: { data?: { errors?: unknown } } }
    | undefined;
  const errorText = errors?.response?.data?.errors
    ? JSON.stringify(errors.response.data.errors)
    : null;

  const submit = () => {
    if (!prefix.trim()) return;
    create.mutate(
      { prefix: prefix.trim(), work_scope: scope, note: note.trim() },
      {
        onSuccess: () => {
          setPrefix("");
          setNote("");
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <PageHeader
        title="Prefiksy zakresu przetargów"
        subtitle="Z czego czytamy, czy przetarg jest na projekt, budowę, czy oba"
        backTo="/tenders"
      />

      <div className="card space-y-2 p-4 text-sm text-[var(--color-text-secondary)]">
        <p>
          Przy imporcie przetargu system czyta <strong>początek nazwy</strong> i stąd
          ustala zakres. Litery „P" i „W" rozumie sam — wielkość liter, spacje i
          separatory nie mają znaczenia, a kolejność też nie:{" "}
          <span className="font-mono">P+W</span>,{" "}
          <span className="font-mono">W + P</span>,{" "}
          <span className="font-mono">p/w</span> i{" "}
          <span className="font-mono">PiW</span> to jeden i ten sam zapis.
        </p>
        <p>
          Tabelę poniżej uzupełnia się wtedy, gdy zamawiający używa własnego oznaczenia
          (np. <span className="font-mono">RKW-2</span>) albo gdy jego skrót znaczy co
          innego, niż podpowiada litera. Wpis z tabeli zawsze wygrywa.
        </p>
        <p className="text-[var(--color-text-tertiary)]">
          Nierozpoznany prefiks nie jest zgadywany — przetarg zostaje z zakresem
          „nieustalony" i widać go na liście pod filtrem „Zakres: nieustalony".
        </p>
      </div>

      <div className="card space-y-3 p-4">
        <p className="section-label">Dodaj prefiks</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-tertiary)]">Prefiks</span>
            <input
              className="input w-40 font-mono"
              value={prefix}
              placeholder="np. RKW-2"
              onChange={(e) => setPrefix(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-tertiary)]">Zakres</span>
            <select
              className="input"
              value={scope}
              onChange={(e) =>
                setScope(e.target.value as Exclude<TenderWorkScope, "">)
              }
            >
              {SCOPE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-xs text-[var(--color-text-tertiary)]">
              Uwagi (opcjonalnie)
            </span>
            <input
              className="input"
              value={note}
              placeholder="np. oznaczenie stosowane przez oddział w Gdańsku"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <Button
            disabled={!prefix.trim() || create.isPending}
            loading={create.isPending}
            onClick={submit}
          >
            Dodaj
          </Button>
        </div>
        {preview && (
          <p className="font-mono text-xs text-[var(--color-text-muted)]">
            Zapisany zostanie jako: {preview}
          </p>
        )}
        {errorText && (
          <p className="text-xs font-mono text-[var(--color-primary)]">{errorText}</p>
        )}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase text-[var(--color-text-tertiary)]">
              <th className="px-3 py-2">Prefiks</th>
              <th className="px-3 py-2">Zapisany jako</th>
              <th className="px-3 py-2">Zakres</th>
              <th className="px-3 py-2">Uwagi</th>
              <th className="px-3 py-2">Stan</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td className="px-3 py-4 section-label" colSpan={6}>
                  Ładowanie…
                </td>
              </tr>
            )}
            {!isLoading && (rows ?? []).length === 0 && (
              <tr>
                <td
                  className="px-3 py-4 text-sm text-[var(--color-text-muted)]"
                  colSpan={6}
                >
                  Brak własnych prefiksów — działa sama reguła liter P / W.
                </td>
              </tr>
            )}
            {(rows ?? []).map((row) => (
              <PrefixRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TenderScopePrefixesPage;
